import { SelectChangeEvent } from '@mui/material'
import { useSnackbar } from 'notistack'
import React, { FC, useEffect, useRef, useState } from 'react'
import { HardwareType, MarkupPropsType, MarkupType, MaterialGroupType, MaterialType } from '../../api/appAPI'
import { EnqueueSnackbarType, showMessage } from '../common/Helpers'
import HardwareUIBox, { ChipType, MaterialChipType } from './HardwareUIBox'

interface IProps {
    hardware: HardwareType
    hardwareList: HardwareType[]
    markups: MarkupType[]
    materials: MaterialType[]
    materialGroups: MaterialGroupType[]

    editHardware(hardware: HardwareType, enqueueSnackbar: EnqueueSnackbarType): void
    addMarkup(markup: MarkupPropsType, enqueueSnackbar: EnqueueSnackbarType): void
    openMaterialDetails(id: number): void
    openMaterialGroupDetails(id: number): void
}

const EditHardware: FC<IProps> = ({ hardware, markups, editHardware, addMarkup, ...props }) => {
    const idRef = useRef(hardware.id)

    const hardwareTypes = hardware.type.split('_')

    const [hardwareProps, setHardwareProps] = useState({
        name: hardware.name,
        sku: hardware.sku,
        description: hardware.description,
        unitWeight: hardware.unitWeight,
        unitPrice: hardware.unitPrice.toString(),
        unitPriceAddon: hardware.unitPriceAddon.toString(),
        addonD: hardware.addonD.toString(),
        addonP: hardware.addonP.toString(),
        minPrice: hardware.minPrice.toString(),
        markupId: hardware.markupId.toString(),
        type: hardwareTypes.length === 2 ? hardwareTypes[0] : hardware.type,
        paramType: hardwareTypes.length === 2 ? hardwareTypes[1] : '',
        childrenChips: hardware.childrenIds.map(childId => {
            const hardwareProps = props.hardwareList.find(h => h.id === childId)
            return hardwareProps ? { id: hardwareProps.id, name: hardwareProps.name } : undefined
        }).filter(childId => childId !== undefined) as ChipType[],
        // materialsChips: props.materials.filter(m => hardware.materialsIds.includes(m.id)).map(m => ({ id: m.id, name: m.name, img: m.thumbnail })),
        materialsChips: hardware.materialsIds.map(id => {
            if (id[0] === 'g') {
                const mGroup = props.materialGroups.find(mGroup => mGroup.id.toString() === id.slice(1))

                if (mGroup) {

                    return ({
                        id: mGroup.id,
                        name: mGroup.name,
                        materials: props.materials.filter(m => mGroup.materialsIds.includes(m.id)).map(m => ({ id: m.id, name: m.name, img: m.thumbnail, isGroup: false })),
                        isGroup: true
                    })
                }
                else return { id: 0 }
            }
            else {
                const material = props.materials.find(material => material.id.toString() === id)

                if (material) {
                    return ({ id: material.id, name: material.name, img: material.thumbnail, isGroup: false })
                }
                else return { id: 0 }
            }
        }) as MaterialChipType[],
        defaultMaterialId: hardware.defaultMaterialId
    })
    const hardwarePropsRef = useRef(hardwareProps)

    const [needSave, setNeedSave] = useState(false)
    const needSaveRef = useRef(needSave)

    const { enqueueSnackbar } = useSnackbar()

    useEffect(() => {
        idRef.current = hardware.id

        const hardwareTypes = hardware.type.split('_')

        setHardwareProps({
            name: hardware.name,
            sku: hardware.sku,
            description: hardware.description,
            unitWeight: hardware.unitWeight,
            unitPrice: hardware.unitPrice.toString(),
            unitPriceAddon: hardware.unitPriceAddon.toString(),
            addonD: hardware.addonD.toString(),
            addonP: hardware.addonP.toString(),
            minPrice: hardware.minPrice.toString(),
            markupId: hardware.markupId.toString(),
            type: hardwareTypes.length === 2 ? hardwareTypes[0] : hardware.type,
            paramType: hardwareTypes.length === 2 ? hardwareTypes[1] : '',
            childrenChips: hardware.childrenIds.map(childId => {
                const hardwareProps = props.hardwareList.find(h => h.id === childId)
                return hardwareProps ? { id: hardwareProps.id, name: hardwareProps.name } : undefined
            }).filter(childId => childId !== undefined) as ChipType[],
            materialsChips: hardware.materialsIds.map(id => {
                if (id[0] === 'g') {
                    const mGroup = props.materialGroups.find(mGroup => mGroup.id.toString() === id.slice(1))

                    if (mGroup) {

                        return ({
                            id: mGroup.id,
                            name: mGroup.name,
                            materials: props.materials.filter(m => mGroup.materialsIds.includes(m.id)).map(m => ({ id: m.id, name: m.name, img: m.thumbnail, isGroup: false })),
                            isGroup: true
                        })
                    }
                    else return { id: 0 }
                }
                else {
                    const material = props.materials.find(material => material.id.toString() === id)

                    if (material) {
                        return ({ id: material.id, name: material.name, img: material.thumbnail, isGroup: false })
                    }
                    else return { id: 0 }
                }
            }) as MaterialChipType[],
            defaultMaterialId: hardware.defaultMaterialId
        })

        setNeedSave(false)
    }, [hardware])

    useEffect(() => {
        hardwarePropsRef.current = hardwareProps
        needSaveRef.current = needSave
    }, [needSave, hardwareProps])

    useEffect(() => () => {
        if (needSaveRef.current) {
            if (window?.confirm('Save hardware?')) {
                handleSaveClick()
            }
        }
    }, [hardware])

    const handleNameChange = (event: React.ChangeEvent<{ value: string }>) => {
        setHardwareProps({
            ...hardwareProps,
            name: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }

    const handleSkuChange = (event: React.ChangeEvent<{ value: string }>) => {
        setHardwareProps({
            ...hardwareProps,
            sku: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }

    const handleDescriptionChange = (event: React.ChangeEvent<{ value: string }>) => {
        setHardwareProps({
            ...hardwareProps,
            description: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }

    const handleTypeChange = (event: SelectChangeEvent<string>) => {
        setHardwareProps({
            ...hardwareProps,
            type: event.target.value,
            materialsChips: [],
            unitPrice: ''
        })
        setNeedSave(true)
    }

    const handleParamTypeChange = (event: SelectChangeEvent<string>) => {
        setHardwareProps({
            ...hardwareProps,
            paramType: event.target.value
        })
        setNeedSave(true)
    }

    const handleUnitWeightChange = (value: number) => {
        setHardwareProps({
            ...hardwareProps,
            unitWeight: value
        })
        setNeedSave(true)
    }

    const handlePriceChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setHardwareProps({
            ...hardwareProps,
            unitPrice: event.target.value as string
        })
        setNeedSave(true)
    }

    const handlePriceAddonChange = (value: string) => {
        setHardwareProps({
            ...hardwareProps,
            unitPriceAddon: value
        })
        setNeedSave(true)
    }

    const handleAddonDChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setHardwareProps({
            ...hardwareProps,
            addonD: event.target.value as string
        })
        setNeedSave(true)
    }

    const handleAddonPChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setHardwareProps({
            ...hardwareProps,
            addonP: event.target.value as string
        })
        setNeedSave(true)
    }

    const handleMinPriceChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setHardwareProps({
            ...hardwareProps,
            minPrice: event.target.value as string
        })
        setNeedSave(true)
    }

    const handleMarkupIdChange = (event: SelectChangeEvent<string>) => {
        setHardwareProps({
            ...hardwareProps,
            markupId: markups.find(m => m.name === event.target.value)?.id.toString() || ''
        })
        setNeedSave(true)
    }

    const deleteChildrenChip = (chip: ChipType) => {
        setHardwareProps({
            ...hardwareProps,
            childrenChips: hardwareProps.childrenChips.filter((ch) => ch.id !== chip.id)
        })
        setNeedSave(true)
    }

    const addChildrenChip = (chip: ChipType) => {
        setHardwareProps({
            ...hardwareProps,
            childrenChips: [...hardwareProps.childrenChips, chip]
        })
        setNeedSave(true)
    }

    const deleteMaterialsChip = (chip: MaterialChipType) => {
        if (hardwareProps.materialsChips.length === 1) {
            setHardwareProps({
                ...hardwareProps,
                unitPrice: '0',
                defaultMaterialId: 0,
                materialsChips: hardwareProps.materialsChips.filter((ch) => ch.id !== chip.id)
            })
        }
        // else if (chip.id === hardwareProps.defaultMaterialId) {
        //     const firstChip = hardwareProps.materialsChips.find(chip => chip.id !== hardwareProps.defaultMaterialId)
        //     setHardwareProps({
        //         ...hardwareProps,
        //         defaultMaterialId: firstChip ? firstChip.id : 0,
        //         materialsChips: hardwareProps.materialsChips.filter((ch) => ch.id !== chip.id)
        //     })
        // }
        else {
            setHardwareProps({
                ...hardwareProps,
                materialsChips: hardwareProps.materialsChips.filter((ch) => ch.id !== chip.id)
            })
        }

        setNeedSave(true)
    }

    const addMaterialsChip = (chip: MaterialChipType) => {
        if (hardwareProps.materialsChips.length === 0) {
            setHardwareProps({
                ...hardwareProps,
                unitPrice: '-1',
                unitWeight: -1,
                defaultMaterialId: chip.id,
                materialsChips: [...hardwareProps.materialsChips, chip]
            })
        }
        else {
            setHardwareProps({
                ...hardwareProps,
                materialsChips: [...hardwareProps.materialsChips, chip]
            })
        }

        setNeedSave(true)
    }

    const handleDefaultMaterialChange = (id: number) => {
        setHardwareProps({
            ...hardwareProps,
            defaultMaterialId: id,
        })
        setNeedSave(true)
    }

    const handleSaveClick = () => {
        const name = hardwarePropsRef.current.name
        const sku = hardwarePropsRef.current.sku
        const description = hardwarePropsRef.current.description
        const type = hardwarePropsRef.current.type
        const paramType = hardwarePropsRef.current.paramType
        const unitWeight = hardwarePropsRef.current.unitWeight
        const unitPrice = hardwarePropsRef.current.unitPrice
        const unitPriceAddon = hardwarePropsRef.current.unitPriceAddon
        const addonD = hardwarePropsRef.current.addonD
        const addonP = hardwarePropsRef.current.addonP
        const minPrice = hardwarePropsRef.current.minPrice
        const markupId = hardwarePropsRef.current.markupId
        const materialsChips = hardwarePropsRef.current.materialsChips
        const defaultMaterialId = hardwarePropsRef.current.defaultMaterialId
        const childrenChips = hardwarePropsRef.current.childrenChips

        if (name === '') {
            showMessage(enqueueSnackbar, "Enter part name")
            return
        }

        if (sku === '') {
            showMessage(enqueueSnackbar, "Enter part sku")
            return
        }

        if (type === '') {
            showMessage(enqueueSnackbar, "Select part type")
            return
        }

        if (type === 'parametric') {
            if (paramType === '') {
                showMessage(enqueueSnackbar, "Select part parameter type")
                return
            }
        }

        if (unitPrice === '') {
            showMessage(enqueueSnackbar, "Enter part unit price")
            return
        }

        if (markupId === '') {
            showMessage(enqueueSnackbar, "Select part markup")
            return
        }

        if (materialsChips.length > 0) {
            let defaultMaterialExist = false

            for (const mChip of materialsChips) {
                if (mChip.isGroup) {
                    for (const chip of mChip.materials) {
                        if (chip.id === defaultMaterialId) {
                            defaultMaterialExist = true
                            break
                        }
                    }
                    if (defaultMaterialExist) break
                }
                else {
                    if (mChip.id === defaultMaterialId) {
                        defaultMaterialExist = true
                        break
                    }
                }
            }

            if (!defaultMaterialExist) {
                showMessage(enqueueSnackbar, "Select default material")
                return
            }
        }

        needSaveRef.current = false
        setNeedSave(false)

        if (type === 'static') {
            const newHardware = {
                id: idRef.current,
                name,
                sku,
                description,
                type,
                unitWeight,
                unitPrice: parseFloat(unitPrice),
                unitPriceAddon: parseFloat(unitPriceAddon),
                addonD: parseFloat(addonD),
                addonP: parseFloat(addonP),
                minPrice: parseFloat(minPrice),
                markupId: parseInt(markupId),
                materialsIds: materialsChips.map(chip => chip.isGroup ? 'g' + chip.id : chip.id.toString()),
                defaultMaterialId,
                childrenIds: childrenChips.map(chip => chip.id),
                editable: hardware.editable
            } as HardwareType
            editHardware(newHardware, enqueueSnackbar)
        }
        else if (type === 'parametric') {
            const newHardware = {
                id: idRef.current,
                name,
                sku,
                description,
                type: type + '_' + paramType,
                unitWeight,
                unitPrice: parseFloat(unitPrice),
                unitPriceAddon: parseFloat(unitPriceAddon),
                addonD: parseFloat(addonD),
                addonP: parseFloat(addonP),
                minPrice: parseFloat(minPrice),
                markupId: parseInt(markupId),
                materialsIds: materialsChips.map(chip => chip.isGroup ? 'g' + chip.id : chip.id.toString()),
                defaultMaterialId,
                childrenIds: childrenChips.map(chip => chip.id),
                editable: hardware.editable
            } as HardwareType
            editHardware(newHardware, enqueueSnackbar)
        }
    }

    return (
        <HardwareUIBox
            title='Edit Part'
            markups={markups}
            hardwareList={props.hardwareList}
            materials={props.materials}
            materialGroups={props.materialGroups}
            addMarkup={addMarkup}
            name={{
                value: hardwareProps.name,
                onChange: handleNameChange
            }}
            sku={{
                value: hardwareProps.sku,
                onChange: handleSkuChange
            }}
            description={{
                value: hardwareProps.description,
                onChange: handleDescriptionChange
            }}
            type={{
                value: hardwareProps.type,
                onChange: handleTypeChange
            }}
            paramType={{
                value: hardwareProps.paramType,
                onChange: handleParamTypeChange
            }}
            unitWeight={{
                value: hardwareProps.unitWeight,
                onChange: handleUnitWeightChange
            }}
            unitPrice={{
                value: hardwareProps.unitPrice,
                onChange: handlePriceChange
            }}
            unitPriceAddon={{
                value: hardwareProps.unitPriceAddon,
                onChange: handlePriceAddonChange
            }}
            addonD={{
                value: hardwareProps.addonD,
                onChange: handleAddonDChange
            }}
            addonP={{
                value: hardwareProps.addonP,
                onChange: handleAddonPChange
            }}
            minPrice={{
                value: hardwareProps.minPrice,
                onChange: handleMinPriceChange
            }}
            markupId={{
                value: hardwareProps.markupId,
                onChange: handleMarkupIdChange
            }}
            button={{
                text: 'Save',
                onClick: handleSaveClick
            }}
            childrenChips={{
                chips: hardwareProps.childrenChips,
                deleteChip: deleteChildrenChip,
                addChip: addChildrenChip
            }}
            materialsChips={{
                chips: hardwareProps.materialsChips,
                deleteChip: deleteMaterialsChip,
                addChip: addMaterialsChip
            }}
            defaultMaterialId={{
                value: hardwareProps.defaultMaterialId,
                set: handleDefaultMaterialChange
            }}

            needSave={needSave}
            editable={hardware.editable === '1'}
            openMaterialDetails={props.openMaterialDetails}
            openMaterialGroupDetails={props.openMaterialGroupDetails}
        />
    )
}

export default EditHardware